import * as React from "react";

import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";

import { nearestTo, type StepDirection, type StepOrigin } from "./layout";
import type { GetNeighbor, TimelineDataStreamer } from "./TimelineDataStreamer";
import type { TimelineTrackItem } from "./TimelineTrackItem";
import type { TimeWindow } from "./window";

export interface NeighborStepOptions<T> {
  /**
   * Searches the whole history; without it, steps see only the streamer's
   * loaded chunk and land nowhere where that chunk cannot answer.
   */
  getNeighbor?: GetNeighbor<T>;
  streamer: TimelineDataStreamer<T> | null;
  /** Where a step starts; `null` starts from the centre of the view. */
  selectedItem: TimelineTrackItem<T> | null;
  timeWindow: TimeWindow | null;
  /** Receives the item a step lands on; without it a step does nothing. */
  onSelect?: (item: TimelineTrackItem<T> | null) => void;
  /** Move the window to show an item without selecting it. */
  reveal: (item: TimelineTrackItem<T>) => void;
}

export interface NeighborSteps {
  /** Select the previous (`-1`) or next (`1`) item; a no-op past either end. */
  step: (direction: StepDirection) => void;
  /**
   * Show the item nearest an empty window, earlier or later, without
   * selecting it. The earlier candidate is the last item to start before the
   * window, so when a longer item started before it and ends closer to the
   * window, the jump measures from the shorter one: finding the latest end
   * would need a search by end time, which `getNeighbor` does not offer.
   */
  jumpToNearest: () => void;
  /**
   * False when a step that way would land nowhere: no `onSelect`, nothing
   * selected and no window to start from, or, without `getNeighbor`, no item
   * the loaded chunk can name. With `getNeighbor` that is never known without
   * asking, so it stays true.
   */
  canStep: Record<StepDirection, boolean>;
}

/**
 * A rejected search lands nowhere, like a `null` answer. An aborted one is
 * expected and dropped silently; any other failure (the page's `getNeighbor`
 * rejecting) is reported, since the user sees only a press that did nothing.
 */
const failedSearch = (signal: AbortSignal) => (error: unknown) => {
  if (!signal.aborted) {
    ErrorMonitoringService.captureError(error, {
      context: { source: "useNeighborSteps" },
    });
  }
};

/**
 * Previous / next for the buttons and the ← → keys, and the empty span's
 * "Jump to nearest", all through one asynchronous search: `getNeighbor`, else
 * the streamer's loaded chunk. Only the latest search applies: starting
 * another one, a change of selection, a change of `getNeighbor` or streamer,
 * or unmounting aborts the one in flight, and an aborted answer is dropped.
 * Two quick presses before the first answer therefore make one step, from the
 * same origin, rather than racing.
 */
export function useNeighborSteps<T>({
  getNeighbor,
  streamer,
  selectedItem,
  timeWindow,
  onSelect,
  reveal,
}: NeighborStepOptions<T>): NeighborSteps {
  const search = React.useCallback<GetNeighbor<T>>(
    (from, direction, signal) =>
      getNeighbor
        ? getNeighbor(from, direction, signal)
        : Promise.resolve(streamer?.neighborOf(from, direction) ?? null),
    [getNeighbor, streamer],
  );

  // A ref, not state: the search in flight never affects what renders.
  const inFlight = React.useRef<AbortController | null>(null);
  const begin = (): AbortSignal => {
    inFlight.current?.abort();
    const controller = new AbortController();
    inFlight.current = controller;
    return controller.signal;
  };

  // Aborts the search in flight so a slow answer cannot override a later
  // selection, made here or anywhere else.
  const selectedId = selectedItem?.id ?? null;
  React.useEffect(() => () => inFlight.current?.abort(), [selectedId, search]);

  const origin: StepOrigin<T> | null =
    selectedItem ??
    (timeWindow === null ? null : (timeWindow.start + timeWindow.end) / 2);

  const step = (direction: StepDirection) => {
    if (onSelect === undefined || origin === null) {
      return;
    }
    const signal = begin();
    search(origin, direction, signal).then((item) => {
      if (!signal.aborted && item !== null) {
        onSelect(item);
      }
    }, failedSearch(signal));
  };

  const jumpToNearest = () => {
    if (timeWindow === null) {
      return;
    }
    const signal = begin();
    Promise.all([
      search(timeWindow.start, -1, signal),
      search(timeWindow.end, 1, signal),
    ]).then(([earlier, later]) => {
      const nearest = nearestTo(timeWindow, earlier, later);
      if (!signal.aborted && nearest !== null) {
        reveal(nearest);
      }
    }, failedSearch(signal));
  };

  // Without `getNeighbor` a step is exactly the streamer's answer, so the
  // button is enabled only when that answer is an item: an unknown one (the
  // chunk still loading, or the origin outside it) would land nowhere too.
  const mayStep = (direction: StepDirection): boolean =>
    onSelect !== undefined &&
    origin !== null &&
    (getNeighbor !== undefined ||
      (streamer?.neighborOf(origin, direction) ?? null) !== null);

  return {
    step,
    jumpToNearest,
    canStep: { [-1]: mayStep(-1), [1]: mayStep(1) },
  };
}
